import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx/xlsx.mjs';

class ExportAgencySweepUtility {
  private static Instance: ExportAgencySweepUtility;
  private static DEFAULT_MESSAGE = 'No records were found.';
  private constructor() {}

  public static getInstance(): ExportAgencySweepUtility {
    if (!ExportAgencySweepUtility.Instance) ExportAgencySweepUtility.Instance = new ExportAgencySweepUtility();

    return ExportAgencySweepUtility.Instance;
  }

  public ExportData(data: IAgencySweepResults[]) {
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';
    const fileName = 'Agency Sweep Report';

    let date = new Date();
    let today =
      (date.getMonth() + 1).toString() + '/' + date.getDate().toString() + '/' + date.getFullYear().toString();

    const title = 'Agency Sweep Report, run on ' + today + ' from Safeco Insurance®';

    let workbook = XLSX.utils.book_new();

    workbook.Props = {
      Title: title,
      Subject: 'Agency Sweep Report',
      Author: 'Safeco Insurance',
      CreatedDate: date,
    };

    this.CleanData(data);
    this.FormatData(title, workbook, data);

    var writeOptions = { bookType: 'xlsx', bookSST: false, type: 'binary', cellStyles: true };
    let excelBuffer = XLSX.write(workbook, writeOptions);

    const excelData = new Blob([this.s2ab(excelBuffer)], { type: fileType });
    FileSaver.saveAs(excelData, fileName + fileExtension);
  }

  private CleanData(data: IAgencySweepResults[]) {
    //ensuring all number values are read as string in excel to resolve number default alignment issues.
    if (data != null) {
      this.FormatExportedData(data);
      data.forEach((element) => {
        element.amount = element?.amount?.toString();
        element.interestNumber = element?.interestNumber?.toString();
      });
    }
  }

  FormatExportedData = (data: IAgencySweepResults[]) => {
    //remove undesired properties from displaying
    let newArray = data.map(function (item: IAgencySweepResults) {
      delete item.userId;
      delete item.masterAgentNumber;
      delete item.forceCode;
      return item;
    });
    return newArray;
  };

  public FormatData(title: string, workbook: any, data: IAgencySweepResults[]) {
    let workSheet = {};
    let sheetName = 'Agency Sweep';
    workSheet = this.ConfigureWorkSheetHeaders(workSheet, title);

    this.PopulateWorkbook(workbook, sheetName, workSheet);

    this.AppendDataToWorkSheet(data, sheetName, workbook);
  }

  SetTableColumnWidths(workbook: any, sheetName: string, wscols: {}[]) {
    // reference for col param types: wch >> "characters", wpx >> "pixels"
    let ws = workbook.Sheets[sheetName];
    ws['!cols'] = wscols;
  }

  AppendDataToWorkSheet = (data: IAgencySweepResults[], sheetName: string, workbook: any) => {
    if (data != null && data.length > 0) {
      XLSX.utils.sheet_add_json(workbook.Sheets[sheetName], data, {
        skipHeader: false,
        origin: 'A3',
        header: ['interestNumber', 'requestDate', 'customerName', 'amount', 'type'],
      });
      let ws = workbook.Sheets[sheetName];
      let headerRange = ['A3', 'B3', 'C3', 'D3', 'E3'];
      for (var i = 0; i < headerRange.length; i++) {
        ws[headerRange[i]].s = {
          font: { bold: true },
        };
      }
      ws['A3'].v = 'Account/policy number';

      let ADD_SPACING = 2;
      var wscols = [
        {
          wch:
            this.GetMaxLength(
              data.map(function (o) {
                return o?.interestNumber ? o?.interestNumber?.toString().length : 0;
              }),
              'Policy Number',
            ) + ADD_SPACING,
        },
        {
          wch:
            this.GetMaxLength(
              data.map(function (o) {
                return o?.requestDate ? 10 : 0;
              }),
              'requestDate',
            ) + ADD_SPACING,
        },
        {
          wch:
            this.GetMaxLength(
              data.map(function (o) {
                return o?.customerName ? o?.customerName?.length : 0;
              }),
              'customerName',
            ) + ADD_SPACING,
        },
        {
          wch:
            this.GetMaxLength(
              data.map(function (o) {
                return o?.amount ? o?.amount?.toString().length : 0;
              }),
              'amount',
            ) + ADD_SPACING,
        },
        {
          wch:
            this.GetMaxLength(
              data.map(function (o) {
                return o?.type ? o?.type?.length : 0;
              }),
              'type',
            ) + ADD_SPACING,
        },
      ];

      this.SetTableColumnWidths(workbook, sheetName, wscols);
    } else {
      let sheet = workbook.Sheets[sheetName];
      XLSX.utils.sheet_add_aoa(sheet, [[ExportAgencySweepUtility.DEFAULT_MESSAGE]], { origin: 'A4' });
      sheet['A4'].s = {
        font: { bold: true, sz: '14' },
      };
    }
  };

  PopulateWorkbook(workbook: any, sheetName: string, worksheet: any) {
    workbook.SheetNames.push(sheetName);
    workbook.Sheets[sheetName] = worksheet;
  }

  private ConfigureWorkSheetHeaders(worksheet: any, headerTitle: string) {
    let cellHeaderFont = { name: 'Arial', bold: true, sz: '22' };
    let cellHeader = this.GenerateCell(headerTitle, cellHeaderFont, null);

    worksheet[XLSX.utils.encode_cell({ c: 0, r: 0 })] = cellHeader;

    return worksheet;
  }

  private GenerateCell = (textValue: string, font: object | null, fill: object | null) => {
    let cell = {
      v: textValue,
      s: {
        font: font,
        fill: fill,
      },
    };

    return cell;
  };

  //function from documentation https://www.npmjs.com/package/js-xlsx
  s2ab(s: string) {
    var buf = new ArrayBuffer(s.length);
    var view = new Uint8Array(buf);
    for (var i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
    return buf;
  }

  private GetMaxLength(mappedFunctionLength: number[], header: string) {
    return Math.max.apply(Math, mappedFunctionLength) > header.length
      ? Math.max.apply(Math, mappedFunctionLength)
      : header.length;
  }
}

export default ExportAgencySweepUtility;
